import React from 'react'
import { Maybe, ProductInterface } from '@pylot-data/fwrdschema'
import { getOfferData, getAggregateRating, getYotpoReviews } from './functions'
import { getTrimmedUrl } from '../../util'
import NextHead from 'next/head'
import { useStoreConfig } from 'config/hooks/useStoreConfig'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'

type ProductJSONProps = {
    product: ProductInterface
    title: string
    description: string
    images?: Maybe<Array<{ url: string }>>
}

const stripHtml = (html: string) => {
    if (html !== '') {
        return html
            .replace(/(<([^>]+)>)/gi, '')
            .replace(/\n|\r/g, ' ')
            .trim()
    }
    return html
}

export const ProductJsonLd = ({
    product,
    title,
    description
}: ProductJSONProps): JSX.Element => {
    const restrictedCategoryText = 'Shisha Tobacco'
    const config = useStoreConfig()
    const shippingSettingsLink = `${config.base.url.baseUrl}content/ordering-info`
    const { variant } = useProductUI(product)
    const isTobacoProduct =
        product?.categories?.filter(
            (category) => category?.name === restrictedCategoryText
        )?.length! > 0

    const sku = variant?.sku

    const getShippingDetails = () => {
        if (isTobacoProduct) {
            return {
                shippingSettingsLink: shippingSettingsLink,
                '@type': 'OfferShippingDetails',
                doesNotShip: true,
                shippingDestination: [
                    {
                        '@type': 'DefinedRegion',
                        addressCountry: 'US',
                        addressRegion: ['WA', 'UT']
                    },
                    {
                        '@type': 'DefinedRegion',
                        addressCountry: ['AU, NZ, AL, DZ, EE, IR, IQ, AF']
                    }
                ]
            }
        } else {
            return {
                shippingSettingsLink: shippingSettingsLink,
                '@type': 'OfferShippingDetails',
                name: 'International',
                doesNotShip: true,
                shippingDestination: {
                    '@type': 'DefinedRegion',
                    addressCountry: ['AU, NZ, AL, DZ, EE, IR, IQ, AF']
                }
            }
        }
    }

    const getImages = () => {
        const imageArray: Array<string> = []

        product.media_gallery?.map((value) => {
            imageArray.push(getTrimmedUrl(value?.url!))
        })
        return imageArray
    }
    const jsonData = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product?.name,
        sku: sku,
        mpn: sku,
        image: getImages(),
        description: stripHtml(product?.short_description?.html!),
        size: product.size_text ? product.size_text : '',
        brand: {
            '@type': 'Brand',
            name: product.brand_text ? product.brand_text : ''
        },
        ...getOfferData(
            product,
            title,
            description,
            config,
            getShippingDetails
        ),

        ...getAggregateRating(product, config),
        ...getYotpoReviews(product)
    }

    return (
        <NextHead>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(jsonData, null, 2)
                }}
            />
        </NextHead>
    )
}
